import { useState, useCallback } from 'react'
import type { Question, Answer, QuizState } from '@/types/extended-types'

interface UseQuizProps {
  questions: Question[]
  onComplete: (answers: Answer[], timeSpentSeconds: number) => void
}

export const useQuiz = ({ questions, onComplete }: UseQuizProps) => {
  const [quizState, setQuizState] = useState<QuizState>({
    currentQuestionIndex: 0,
    answers: [],
    startTime: Date.now(),
  })

  const currentQuestion = questions[quizState.currentQuestionIndex]
  const isLastQuestion = quizState.currentQuestionIndex === questions.length - 1
  const progress = ((quizState.currentQuestionIndex + 1) / questions.length) * 100

  // Get current answer for the question
  const getCurrentAnswer = useCallback(() => {
    return quizState.answers.find(
      answer => answer.questionId === currentQuestion?.id
    )?.answer || ''
  }, [quizState.answers, currentQuestion?.id])

  // Set answer for current question
  const setAnswer = useCallback((answer: string) => {
    if (!currentQuestion) return

    setQuizState(prev => {
      const newAnswers = prev.answers.filter(a => a.questionId !== currentQuestion.id)
      newAnswers.push({
        questionId: currentQuestion.id,
        answer: answer,
      })

      return {
        ...prev,
        answers: newAnswers,
      }
    })
  }, [currentQuestion])

  // Navigate to next question
  const goToNext = useCallback(() => {
    if (isLastQuestion) {
      // Complete the quiz
      const endTime = Date.now()
      setQuizState(prev => ({ ...prev, endTime }))

      // Calculate time spent in seconds
      const timeSpentMilliseconds = endTime - quizState.startTime
      const timeSpentSeconds = Math.floor(timeSpentMilliseconds / 1000)

      // Trim answers before completing
      const trimmedAnswers = quizState.answers.map(answer => ({
        ...answer,
        answer: answer.answer.trim()
      }))

      onComplete(trimmedAnswers, timeSpentSeconds)
    } else {
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
      }))
    }
  }, [isLastQuestion, quizState.answers, quizState.startTime, onComplete])

  // Navigate to previous question
  const goToPrevious = useCallback(() => {
    if (quizState.currentQuestionIndex > 0) {
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1,
      }))
    }
  }, [quizState.currentQuestionIndex])

  // Check if current answer is valid
  const isCurrentAnswerValid = useCallback(() => {
    const currentAnswer = getCurrentAnswer()
    return currentAnswer.trim().length > 0
  }, [getCurrentAnswer])

  // Can go to next question
  const canGoNext = isCurrentAnswerValid()

  // Can go to previous question
  const canGoPrevious = quizState.currentQuestionIndex > 0

  return {
    currentQuestion,
    currentQuestionIndex: quizState.currentQuestionIndex,
    totalQuestions: questions.length,
    progress,
    isLastQuestion,
    getCurrentAnswer,
    setAnswer,
    goToNext,
    goToPrevious,
    canGoNext,
    canGoPrevious,
    answers: quizState.answers,
  }
}